<template>
  <div>
    <apexchart type="bar" :options="chartOptions" :series="series" width="100%" height="400" />
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts"
import Swal from "sweetalert2";
import AlertasDataService from "../../../services/AlertasDataService";
import Crypto from "../../../services/Crypto";


export default {
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    enum: {
      type: Number,
      default: 0,
    },
    rawData: {
      type: [Array, Object],
      required: true, // Pasar datos desde la consulta a la base de datos
    },
    fechas: {
      type: Object,
      default: () => ({}),
    },
    total: {
      type: Number,
      default: 0,
    },
    id: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      key: sessionStorage.getItem("key"),
      series: [],
      chartOptions: {},
      dateMapping: [], // Relación entre el índice del eje X y la fecha real
    };
  },
  watch: {
    rawData: {
      immediate: true,
      handler(newData) {
        this.processData(newData);
      },
    },
  },
  methods: {
    processData(data) {
      // Preparar series y etiquetas para la gráfica
      const todas = ["Auxilio", "Batería baja", "Geocerca", "Impacto", "Movimiento", "Velocidad"];
      const geocercas = ["Entrada", "Salida"];

      const valores = Object.keys(data[0][1]).length;

      let categories = valores == 2 ? geocercas : todas;

      const series = categories.map((category) => ({
        name: category,
        type: "bar",
        data: [],
      }));
      const xAxisCategories = [];
      const dateMapping = [];
      const labels = [];

      data.forEach((entry) => {
        const [date, values, isAllZero, dayLabel] = entry;

        // Configurar eje X (mostrar dayLabel)
        xAxisCategories.push(dayLabel || date);
        dateMapping.push(date);

        if (isAllZero) {
          // Agregar columna con etiqueta "Sin datos"
          series.forEach((serie) => {
            serie.data.push(null); // Usar null para que no aparezca un círculo
          });
          labels.push({
            x: dayLabel || date, // Posicionamos la anotación en el eje X
            y: 0, // Posicionamos la anotación en el eje Y
            text: "Sin datos", // Texto de la anotación
            borderColor: "#FF4560", // Color de borde para el texto
            borderWidth: 1,
            fontSize: "12px",
            offsetX: 0, // Ajuste horizontal
            offsetY: -10, // Ajuste vertical (sólo se aplica a las anotaciones de texto)
            color: "#FF4560", // Color de texto
          });
        } else {
          // Cargar valores reales para cada categoría
          categories.forEach((category, index) => {
            series[index].data.push(values[category] || 0);
          });
        }
      });

      // Actualizar las series y opciones del gráfico
      this.series = series;
      this.dateMapping = dateMapping;
      this.chartOptions = {
        chart: {
          zoom: {
        enabled: true, // Permitir zoom
        type: 'x', // Zoom horizontal
        autoScaleYaxis: true, // Ajustar automáticamente el eje Y al hacer zoom
          },
          events: {
        dataPointSelection: (event, chartContext, config) => {
          if (config.dataPointIndex !== -1) {
            const { seriesIndex, dataPointIndex } = config;

            const data = {
          id: this.id,
          category: this.series[seriesIndex].name,
          date: this.dateMapping[dataPointIndex],
            };
            this.handleBarClick(data);
          }
        },
          },
          stacked: true,
          toolbar: {
        show: true,
        tools: {
          zoom: true, // Mostrar botón de zoom
          zoomin: true, // Mostrar botón de acercar
          zoomout: true, // Mostrar botón de alejar
          reset: true, // Mostrar botón de resetear zoom
        },
          },
        },
        title: {
          text: `Alertas del ${this.fechas.fechaInicial} al ${this.fechas.fechaFinal}: ${this.total}`,
          align: "center",
        },
        xaxis: {
          categories: xAxisCategories, // Mostrar los labels (dayLabel)
          title: { text: "Alertas por fecha" },
          labels: { rotate: -45 },
        },
        yaxis: {
          title: { text: "Número de alertas" },
        },
        plotOptions: {
          bar: {
        columnWidth: "50%",
        borderRadius: 5,
          },
        },
        annotations: {
          points: labels, // Agregar etiquetas de "Sin datos"
        },
        fill: {
          opacity: 1,
        },
        colors: ["#008FFB", "#FEB019", "#00E396", "#FF4560", "#775DD0", "#4CAF50"],
        legend: {
          position: "top",
          horizontalAlign: "center",
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: {
        formatter: (value, { dataPointIndex }) => this.dateMapping[dataPointIndex], // Mostrar fecha real
          },
        },
      };
    },

     async handleBarClick(data) {
      try {
        let response = null;
        if (this.enum === 1) {
          response = await AlertasDataService.getAlertsByDatesAndType(data, this.key)
        } else if (this.enum === 2) {
          response = await AlertasDataService.getGeofencesByDatesAndType(data, this.key);
        } else if(this.enum === 3) {
          response = await AlertasDataService.getDeviceAlertsByDateAndType(data, this.key);
        } else if(this.enum === 4) {
          response = await AlertasDataService.getDeviceGeofencesByDateAndType(data, this.key);
        }
        const decoded = await Crypto.desencriptar(response.data, this.key);
        const countRegistros = decoded.length;

        Swal.fire({
            title: `Alertas de ${data.category}`,
            html: `
                <div class="table-wrapper">
                    <table class="fl-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Dispositivo</th>
                                <th>Ubicación</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${decoded
                    .map(
                        (item) => `
                            <tr>
                                <td>${this.displayTime(
                            item.fechaCreacion
                        )}</td>
                                <td>${item.dispositivo.nombre}</td>
                                <td>
                                    <a href="${item.link
                            }"target=_blank">
                                        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 48 48">
                                            <path fill="#1c9957" d="M42,39V9c0-1.657-1.343-3-3-3H9C7.343,6,6,7.343,6,9v30c0,1.657,1.343,3,3,3h30C40.657,42,42,40.657,42,39z"></path>
                                            <path fill="#3e7bf1" d="M9,42h30c1.657,0-15-16-15-16S7.343,42,9,42z"></path>
                                            <path fill="#cbccc9" d="M42,39V9c0-1.657-16,15-16,15S42,40.657,42,39z"></path>
                                            <path fill="#efefef" d="M39,42c1.657,0,3-1.343,3-3v-0.245L26.245,23L23,26.245L38.755,42H39z"></path>
                                            <path fill="#ffd73d" d="M42,9c0-1.657-1.343-3-3-3h-0.245L6,38.755V39c0,1.657,1.343,3,3,3h0.245L42,9.245V9z"></path>
                                            <path fill="#d73f35" d="M36,2c-5.523,0-10,4.477-10,10c0,6.813,7.666,9.295,9.333,19.851C35.44,32.531,35.448,33,36,33s0.56-0.469,0.667-1.149C38.334,21.295,46,18.813,46,12C46,6.477,41.523,2,36,2z"></path>
                                            <path fill="#752622" d="M36 8.5A3.5 3.5 0 1 0 36 15.5A3.5 3.5 0 1 0 36 8.5Z"></path>
                                            <path fill="#fff" d="M14.493,12.531v2.101h2.994c-0.392,1.274-1.455,2.185-2.994,2.185c-1.833,0-3.318-1.485-3.318-3.318s1.486-3.318,3.318-3.318c0.824,0,1.576,0.302,2.156,0.799l1.548-1.547C17.22,8.543,15.92,8,14.493,8c-3.038,0-5.501,2.463-5.501,5.5s2.463,5.5,5.501,5.5c4.81,0,5.637-4.317,5.184-6.461L14.493,12.531z"></path>
                                        </svg>
                                    </a>
                                </td>
                            </tr>`
                    )
                    .join("")}
                        </tbody>
                    </table>
                </div>
                <div class="lblRegistros">Total de registros: ${countRegistros}</div>
            `,
            width: 700,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Aceptar",
        });
      } catch (e) {
        console.error(e);
      }
    },
    displayTime(date) {
      let options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      };
      let fecha = new Date(date)
        .toLocaleDateString("es-ES", options)
        .toString();
      fecha = fecha[0].toUpperCase() + fecha.substring(1);
      return fecha;
    },
  },
};
</script>